export function asList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.list)) return payload.data.list
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  if (Array.isArray(payload?.data?.results)) return payload.data.results
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

export function totalOf(payload, fallback = 0) {
  if (typeof payload?.total === 'number') return payload.total
  if (typeof payload?.count === 'number') return payload.count
  if (typeof payload?.data?.total === 'number') return payload.data.total
  if (typeof payload?.data?.count === 'number') return payload.data.count
  return fallback
}

export function modelTitle(model) {
  if (!model) return 'Unknown model'
  return model.of_email || model.email || model.name || `Model #${model.id}`
}

export function providerLinks(model) {
  return Array.isArray(model?.provider_links) ? model.provider_links : []
}

export function providerLabel(provider) {
  const key = String(provider || '').toLowerCase()
  if (key === 'onlyfansapi' || key === 'onlyfans') return 'OnlyFans'
  if (key === 'onlymonster') return 'OnlyMonster'
  if (key === 'fanltv_ofapi') return 'FanLTV OFAPI Backup'
  if (key === 'fanvue') return 'Fanvue'
  if (key === 'telegram' || key === 'telegram_account') return 'Telegram'
  if (key === 'telegram_tribute' || key === 'tribute') return 'Telegram Tribute'
  if (key === 'telegram_bot_payments' || key === 'telegram_payments') return 'Telegram Bot Payments'
  if (key === 'aigirlfactory' || key === 'aigf') return 'AIGirlFactory'
  if (key === 'fansly') return 'Fansly'
  if (key === 'instagram') return 'Instagram'
  if (key === 'custom_api') return 'Custom API'
  return provider || 'Provider'
}

export function providerStatus(link) {
  return String(link?.connection_status || link?.status || 'not_connected').toLowerCase()
}

export function providerWebhookStatus(link) {
  return String(link?.webhook_status || '').toLowerCase()
}

export function providerWebhookDetail(link) {
  if (!providerWebhookStatus(link)) return 'No webhook delivery'
  const updated = link?.webhook_updated_at ? formatDate(link.webhook_updated_at) : 'No delivery time'
  const error = String(link?.webhook_error || '').trim()
  return error ? `${updated} - ${error}` : updated
}

export function statusTone(status) {
  const s = String(status || '').toLowerCase()
  if (['connected', 'active', 'ok', 'ready', 'completed', 'success', 'passed'].includes(s)) return 'good'
  if (['connecting', 'queued', 'running', 'syncing', 'pending', 'in_progress', 'partial'].includes(s)) return 'warn'
  if (['failed', 'error', 'blocked', 'disconnected'].includes(s)) return 'bad'
  return 'neutral'
}

export function hasConnectedProvider(model) {
  return providerLinks(model).some((link) => providerStatus(link) === 'connected')
}

export function setupChecklist(model, folders = []) {
  const links = providerLinks(model)
  return [
    {
      key: 'connection',
      label: 'Platform connection',
      done: links.some((link) => providerStatus(link) === 'connected'),
      help: 'At least one platform account must be connected.',
    },
    {
      key: 'profile',
      label: 'Model profile',
      done: Boolean(String(model?.account_description || '').trim()),
      help: 'Bot needs persona and account context.',
    },
    {
      key: 'timezone',
      label: 'Timezone',
      done: Boolean(String(model?.timezone || '').trim()),
      help: 'Used for proactive timing and quiet hours.',
    },
    {
      key: 'automation',
      label: 'Automation mode',
      done: Boolean(model?.automation_mode),
      help: 'Defines when bot acts alone or asks for handoff.',
    },
    {
      key: 'content',
      label: 'Content source',
      done: folders.length > 0 || Number(model?.media_count || model?.media_total || 0) > 0,
      help: 'Media folders or imported content should be available.',
    },
  ]
}

export function folderValue(folder) {
  if (!folder) return ''
  const source = String(folder.source || '').trim()
  const kind = String(folder.kind || '').trim().toLowerCase()
  if (source && kind !== 'folder') return `source:${source}`
  if (source && folder.id != null && folder.id !== '') return `source:${source}|id:${folder.id}`
  if (source && folder.name) return `source:${source}|name:${folder.name || ''}`
  if (folder.id != null && folder.id !== '') return `id:${folder.id}`
  return `name:${folder.name || ''}`
}

export function folderPayload(value) {
  if (!value) return {}
  if (value.startsWith('source:') && value.includes('|')) {
    const [sourcePart, scopePart] = value.split('|', 2)
    const source = sourcePart.slice(7)
    if (scopePart?.startsWith('id:')) return { source, folder_id: scopePart.slice(3) }
    if (scopePart?.startsWith('name:')) return { source, folder_name: scopePart.slice(5) }
    return { source }
  }
  if (value.startsWith('source:')) return { source: value.slice(7) }
  if (value.startsWith('id:')) return { folder_id: value.slice(3) }
  if (value.startsWith('name:')) return { folder_name: value.slice(5) }
  return {}
}

export function selectedFolderLabel(folders, value) {
  if (!value) return 'All content sources'
  const folder = folders.find((item) => folderValue(item) === value)
  return folder?.name || value.replace(/^source:/, '').replace(/^id:/, '#').replace(/^name:/, '') || 'Selected source'
}

export function platformFromConversation(row) {
  const key = String(row?.platform || row?.provider || row?.source || 'onlyfansapi').toLowerCase()
  if (key === 'onlyfans') return 'onlyfansapi'
  if (key === 'telegram') return 'telegram_account'
  if (key === 'tribute') return 'telegram_tribute'
  if (key === 'telegram_payments') return 'telegram_bot_payments'
  if (key === 'aigf') return 'aigirlfactory'
  return key || 'onlyfansapi'
}

export function formatDate(value) {
  if (!value) return 'No data'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString()
}

export function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

export function compactNumber(value) {
  const n = Number(value || 0)
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}
